import React from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, IconButton } from '@mui/material';
import { DataImportStatusEnum } from '~/graphql/__generated__/graphql';
import { format } from 'date-fns';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplayIcon from '@mui/icons-material/Replay';
import { startCase } from 'lodash';
import { CustomDataImportSelectItem } from '../graphqlQueries';
import { useZonedDateFormat } from '~/providers/ZonedDateFormatProvider';

interface ImportTableProps {
  data: CustomDataImportSelectItem[];
  onRowClick: (row: CustomDataImportSelectItem) => void;
  onDeleteDataImport: (row: CustomDataImportSelectItem) => void;
  onPreviewDataImport: (row: CustomDataImportSelectItem) => void;
}

type StatusKey = DataImportStatusEnum | 'Finished' | 'Finished with errors';

export const dataImportStatusColorMap: Record<StatusKey, string> = {
  [DataImportStatusEnum.InProgress]: 'secondary',
  [DataImportStatusEnum.Pending]: 'default',
  [DataImportStatusEnum.Uploaded]: 'primary',
  [DataImportStatusEnum.Successful]: 'success',
  'Finished': 'success',
  'Finished with errors': 'warning',
  [DataImportStatusEnum.Failed]: 'error',
};

export const formatDataImportStatus = (dataImport: CustomDataImportSelectItem): StatusKey => {
  const errors = JSON.parse(`${dataImport.metadata}`)?.errors?.length;
  if (dataImport.status === DataImportStatusEnum.Successful && (errors > 0)) {
    return 'Finished with errors';
  }

  if (dataImport.status === DataImportStatusEnum.Successful) {
    return 'Finished';
  }
  
  return dataImport.status;
}

const ImportTable: React.FC<ImportTableProps> = ({ data, onRowClick, onDeleteDataImport, onPreviewDataImport }) => {
  const { datef } = useZonedDateFormat();
  return (
    <TableContainer component={Box} sx={{ boxShadow: 1, borderRadius: 1 }}>
      <Table data-testid="import-history-table">
        <TableHead>
          <TableRow>
            <TableCell>Import</TableCell>
            <TableCell>Filename</TableCell>
            <TableCell>User</TableCell>
            <TableCell align="right">Created</TableCell>
            <TableCell align="right">Updated</TableCell>
            <TableCell align="right">Errors</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow
              key={index}
              onClick={() => {
                if (row.status === DataImportStatusEnum.InProgress || row.status === DataImportStatusEnum.Pending) {
                  return;
                }
                
                if (row.status === DataImportStatusEnum.Uploaded) {
                  onPreviewDataImport(row);
                  return;
                }

                onRowClick(row);
              }}
              sx={{
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <TableCell>{datef(new Date(row.createdAt), "MMMM dd, yyyy 'at' hh:mm a")}</TableCell>
              <TableCell>{row.filename}</TableCell>
              <TableCell>{row.createdBy?.firstName} {row.createdBy?.lastName}</TableCell>
              <TableCell align="right">{(JSON.parse(`${row.metadata}`))?.created_count ?? '-'}</TableCell>
              <TableCell align="right">{(JSON.parse(`${row.metadata}`))?.updated_count ?? '-'}</TableCell>
              <TableCell align="right">{(JSON.parse(`${row.metadata}`))?.errors?.length ?? '-'}</TableCell>
              <TableCell>
                <Chip
                  label={startCase(formatDataImportStatus(row))}
                  color={dataImportStatusColorMap[`${formatDataImportStatus(row)}`] as 'warning' | 'success' | 'error' | 'default'}
                  sx={{
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                  size='small'
                />
              </TableCell>
              <TableCell>
                {(row.status !== DataImportStatusEnum.Successful && row.status !== DataImportStatusEnum.InProgress) && (
                  <IconButton data-testid="delete-import-button" onClick={(e) => {
                    e.stopPropagation();
                    onDeleteDataImport(row);
                  }}>
                    <DeleteIcon />
                  </IconButton>
                )}
                {(row.status === DataImportStatusEnum.Uploaded) && (
                  <IconButton onClick={(e) => {
                    e.stopPropagation();
                    onPreviewDataImport(row);
                  }}>
                    <ReplayIcon />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ImportTable;
